import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Square, Printer, Sun, Palette, Camera, Sparkle, Crosshair, GridFour } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// Visual: an 18% gray block sitting between pure black and pure white so the
// student can see what "middle gray" actually looks like.
function GrayCardVisual({ lang }) {
  const labels = lang === "es"
    ? { black: "Negro (0%)", gray: "Gris 18% — referencia neutra", white: "Blanco (100%)" }
    : { black: "Pure Black (0%)", gray: "18% Gray — neutral reference", white: "Pure White (100%)" };
  return (
    <div className="w-full">
      <div className="grid grid-cols-3 gap-1.5 rounded-md overflow-hidden border border-white/10">
        <div className="h-24 bg-black flex items-end justify-center pb-1.5">
          <span className="text-white/60 text-[10px] font-mono">{labels.black}</span>
        </div>
        <div className="h-24 flex items-end justify-center pb-1.5" style={{ backgroundColor: "#777777" }}>
          <span className="text-black/80 text-[11px] font-mono font-bold">{labels.gray}</span>
        </div>
        <div className="h-24 bg-white flex items-end justify-center pb-1.5">
          <span className="text-black/60 text-[10px] font-mono">{labels.white}</span>
        </div>
      </div>
    </div>
  );
}

// Visual: a 4x6 grid recreating the SpyderCHECKR 24 layout — top 18 color
// patches + bottom 6 grayscale patches. Colors are approximate visual
// references, NOT a true colorimetric standard. The bottom row is the
// grayscale strip that includes the 18% gray reference.
function SpyderChecker24Visual({ lang }) {
  // Approximate SpyderCHECKR 24 colors (sRGB visual approximations)
  const colorRows = [
    ["#7C5246", "#C4998C", "#8094A8", "#739067", "#8784A9", "#6FB7B1"], // skin tones + nature
    ["#D88B45", "#5085AD", "#C25C5C", "#5C4D85", "#A8B845", "#DDA53C"], // primaries / vivid
    ["#3F66A8", "#3F8845", "#A2334A", "#E5C13D", "#C5468C", "#1C8197"], // saturated mix
  ];
  const grayRow = ["#FFFFFF", "#CCCCCC", "#999999", "#666666", "#333333", "#000000"];
  const grayLabels = lang === "es"
    ? ["100%", "80%", "60%", "40%", "20%", "0%"]
    : ["100%", "80%", "60%", "40%", "20%", "0%"];
  const heading = lang === "es"
    ? "Diseño SpyderCHECKR 24 (referencia visual)"
    : "SpyderCHECKR 24 Layout (visual reference)";
  const colorLabel = lang === "es" ? "18 parches de color" : "18 color patches";
  const grayLabel = lang === "es" ? "6 parches de gris (incluye gris 18%)" : "6 grayscale patches (includes 18% gray)";

  return (
    <div className="w-full bg-[#1a1a1d] border border-white/10 rounded-lg p-3 sm:p-4">
      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold mb-2">{heading}</p>
      <div className="space-y-1.5">
        <div className="text-[10px] text-[#A1A1AA] mb-1">{colorLabel}</div>
        {colorRows.map((row, i) => (
          <div key={i} className="grid grid-cols-6 gap-1">
            {row.map((c, j) => (
              <div key={j} className="aspect-square rounded-sm border border-black/20" style={{ backgroundColor: c }} />
            ))}
          </div>
        ))}
        <div className="text-[10px] text-[#A1A1AA] mt-3 mb-1">{grayLabel}</div>
        <div className="grid grid-cols-6 gap-1">
          {grayRow.map((c, i) => (
            <div key={i} className="aspect-square rounded-sm border border-black/30 flex items-end justify-center pb-0.5" style={{ backgroundColor: c }}>
              <span className={`text-[9px] font-mono ${i < 3 ? "text-black/70" : "text-white/70"}`}>{grayLabels[i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CONTENT = {
  en: {
    page_title: "The Gray Card: Color Accuracy & Exposure Precision",
    page_sub: "A flat, neutral piece of cardboard that reflects exactly 18% of the light hitting it. Sounds simple — but it is the single most reliable tool for nailing exposure and color in any genre: product, portrait, lifestyle, or commercial.",
    download: "Download as PDF",
    generating: "Generating...",

    intro_title: "What is an 18% Gray Card?",
    intro_body: "An 18% gray card is a flat, matte reference target that reflects 18% of the light that hits it. It looks like ordinary gray cardboard. But that exact 18% reflectance is what every camera light meter on Earth is calibrated for: when you point a camera at the world, the meter assumes the average tone is 18% gray and sets exposure to make THAT tone come out as middle gray in the image. So if your scene actually IS middle gray (because the card fills the frame), the meter is right. If your scene is NOT (a bride in a white dress, a black cat on coal), the meter is wrong — and the card fixes it.",
    intro_uses: [
      "Set perfect exposure in high-contrast scenes (bright white snow, dark studio backdrops)",
      "Calibrate white balance under any lighting — LED, flash, mixed tungsten + daylight",
      "Bridge color between multiple cameras (DSLR + smartphone + drone) in post",
      "Spot hot spots, color casts, and unwanted reflections on glossy products",
      "Confirm tack-sharp focus at f/8-f/11 using the card's high-contrast markings",
    ],
    intro_visual_caption: "The 18% gray sits exactly between pure black and pure white in luminance — the neutral 'middle' your camera expects.",

    meter_trap_title: "1. Overcoming the Internal Meter Trap",
    meter_trap_body: "Camera meters average the scene to 18% gray. That works for an average scene. It fails when the scene is dominated by very bright or very dark tones — because the camera will try to push that dominant tone TOWARD 18% gray, ruining the exposure.",
    meter_trap_examples: [
      { scene: "Bride in white dress against white wall", trap: "Meter sees 'too bright' → underexposes → dress turns gray", fix: "Place the card in the same light, meter ON the card, lock that exposure, then shoot the bride." },
      { scene: "Black cat on black couch", trap: "Meter sees 'too dark' → overexposes → fur turns muddy gray", fix: "Same drill: meter on a card held in the same light as the cat, lock exposure, recompose." },
      { scene: "Snowy mountain landscape", trap: "Meter underexposes the snow into dirty gray slush", fix: "Drop the card on the snow, take a meter reading, use those settings — snow comes out properly white." },
      { scene: "Backlit silhouette at sunset", trap: "Meter exposes for the bright sky → subject goes pitch black", fix: "Meter off the card held in the SAME ambient light as the subject (not the sky), and the subject's face will be properly exposed." },
    ],
    meter_trap_workflow_title: "5-step meter workflow",
    meter_trap_workflow: [
      "Switch camera to Spot Metering mode",
      "Hold the gray card in the SAME light as your subject (not in shadow if the subject is in sun)",
      "Fill the viewfinder with the card and press the meter button (half-press shutter or AE-L)",
      "Note the recommended exposure (or use AE-Lock to hold it)",
      "Remove the card, recompose on your subject, and shoot. The exposure is now correct for THAT light, regardless of how bright or dark your subject is.",
    ],

    wb_title: "2. Custom White Balance Mastery",
    wb_body: "Every light source has its own color signature. LEDs are often slightly green; tungsten is orange; flash is bluish-neutral; cloudy daylight is blue-cool. Auto White Balance guesses — and guesses wrong in mixed light. A gray card gives the camera the truth: 'here is something you KNOW should be neutral. Adjust everything else accordingly.'",
    wb_steps_title: "How to set Custom WB with a gray card",
    wb_steps: [
      "Set the camera mode that will be used for the shoot (manual, aperture priority — doesn't matter for WB)",
      "Hold the gray card in the exact light your subject will be in. Fill the frame with the card (no other elements).",
      "Take ONE photo of the card. The card image looks gray — slightly tinted by the ambient light.",
      "Camera menu → White Balance → Custom WB → 'Set from image' → select the card photo you just took.",
      "Camera now applies a precise WB calibrated to THIS light. Any further shots are color-accurate.",
      "Re-shoot the card if you move to a different room, the sun shifts, or the lighting changes.",
    ],
    wb_genres_title: "Genre-by-genre wins",
    wb_genres: [
      { name: "Product photography", body: "Skin tones don't matter, but accurate product color is everything. A gray card in EVERY new lighting setup is non-negotiable for e-commerce work." },
      { name: "Portrait", body: "Skin tones live or die on white balance. A 1-second card shot at the start of a session saves hours of skin-tone correction in Lightroom." },
      { name: "Lifestyle / events", body: "Mixed lighting (window light + indoor LEDs + candles) confuses every AWB engine. Custom WB from a card gives consistent skin tones across the whole event." },
      { name: "Real estate / interiors", body: "Tungsten lamps + daylight + fluorescent kitchen = three different color casts in one frame. A card shot lets you batch-correct the entire shoot to one neutral baseline." },
    ],

    multicam_title: "3. Multi-Camera Color Synchronization",
    multicam_body: "Modern shoots routinely mix bodies: a DSLR for hero shots, a smartphone for behind-the-scenes, a drone for aerials, a friend's mirrorless for a second angle. Every sensor renders color differently. Without a reference, edits look like a patchwork. With a gray card, you have a visual 'tuning fork' that anchors every camera to the same neutral baseline.",
    multicam_steps: [
      "At the start of the shoot, gather every camera that will be used.",
      "In the same light, have each camera shoot ONE frame of the gray card filling the frame.",
      "Label or note which frame corresponds to which camera.",
      "In Lightroom or Capture One, use the eyedropper on the gray card in each camera's reference frame. Set that as the camera's WB reference profile.",
      "Apply that camera's WB profile to all the other frames from THAT camera in the session.",
      "Result: all cameras now share the SAME neutral baseline. Skin tones, product colors, and ambient look match across the edit.",
    ],

    gloss_title: "4. Managing High-Gloss and Reflective Surfaces",
    gloss_body: "Plastic, glass, polished metal, jewelry, electronics, and lacquered cosmetics packaging all reflect light back into the camera. Worse, they reflect COLOR from nearby objects (a red couch reflecting into the side of a white perfume bottle = pink contamination). A gray card is the cleanest 'witness' for spotting these problems before they wreck the shot.",
    gloss_steps: [
      "Place the gray card next to (or behind) the reflective product so it sits in the same light path.",
      "Take a test frame. Look at the card: does it show a color tint? A blue tint means there's blue light spilling onto the set (cool window, blue wall). A green tint means LEDs. A red tint means a warm bounce.",
      "If the card is tinted, the product is being contaminated by that SAME tint — even if your eye doesn't catch it.",
      "Fix the spill: change the offending bounce, kill the leak with black flag, swap the LED, or color-correct in post using the card as the neutral reference.",
      "For hot spots: angle the card to mimic the surface of the product. If the card shows a bright reflection of a light source at that angle, the product will too. Move the light or add a diffuser.",
    ],
    gloss_examples_title: "Where this matters most",
    gloss_examples: [
      "Watches, jewelry, and chrome accessories",
      "Glossy plastic phone cases and laptop lids",
      "Wine bottles, perfume bottles, and cosmetics packaging",
      "Lacquered furniture, cars, and motorcycle paint",
      "Headshots where the subject wears glasses (catchlights vs. unwanted glare)",
    ],

    focus_title: "5. Focus & Sharpness Verification",
    focus_body: "Many professional gray cards include high-contrast markings — black bars, fine grid lines, or sharp text printed on the gray surface. These markings are gold for verifying whether your camera is achieving TACK-SHARP focus, especially at smaller apertures (f/8, f/11, f/16) where slight focus errors hide easily.",
    focus_steps: [
      "Mount your camera on a tripod, the gray card at the same distance you'd shoot your real subject.",
      "Use the camera's center focus point on the high-contrast markings of the card.",
      "Shoot at f/2.8 (shallow), f/5.6, f/8, and f/11. One frame at each aperture.",
      "Zoom into each photo at 100%. The markings should appear razor-sharp.",
      "If a frame is soft → your lens has front- or back-focus calibration issues at that aperture. Most pro bodies have AF micro-adjustment (Canon: AFMA, Nikon: AF Fine Tune, Sony: AF Micro Adj).",
      "Use the gray card markings + manual AFMA to dial in your lens body-by-body for tack-sharp results.",
    ],
    focus_pro: "Pro tip: 95% of 'soft' photos at f/11 are actually a focus problem, NOT a diffraction problem. Verify with a gray card before blaming the lens.",

    spyder_title: "6. Leveling Up: The 24-Patch Color Checker (Datacolor SpyderCHECKR 24)",
    spyder_body: "A gray card solves two things: exposure and white balance. The Datacolor SpyderCHECKR 24 solves a THIRD: precise, repeatable color accuracy across the 24 color patches printed on the card — including six skin tones, primaries, secondaries, and a grayscale strip that includes the same 18% gray reference you already know. Think of it as the gray card upgraded with a calibrated color palette built in.",
    spyder_what: [
      "24 calibrated color patches: 18 color targets + 6 grayscale steps (including 18% middle gray, pure white, and pure black).",
      "Pocket-size (~4 x 6 inches), folds in half for protection, hangs from a lanyard.",
      "Includes Datacolor's SpyderCHECKR software (free with the card) for creating custom DNG Color Profiles (DCP) for Lightroom Classic, or ICC profiles for Capture One and Photoshop.",
      "Far more powerful than a gray card alone — but every gray card workflow you just learned still works using the SpyderCHECKR's gray patch."
    ],
    spyder_when: [
      "Product photography and e-commerce where exact product color is the entire point",
      "Studio portrait work where skin tones must match across multiple sessions",
      "Fine art reproduction (copying paintings, antique documents, fabric swatches)",
      "Video production where one scene is graded and the rest must match",
      "Any time the client says 'match the color of this physical object exactly'"
    ],
    spyder_workflow_title: "Field workflow — at the shoot",
    spyder_workflow: [
      "At the START of every new lighting setup, place the SpyderCHECKR in the SAME light as your subject. Hold it flat-on to the camera, no shadow across it, no glare.",
      "Fill 30-50% of the frame with the card (it does NOT need to fill the whole frame — only enough to read each patch later in software).",
      "Shoot ONE frame at the same camera settings (manual mode preferred) you'll use for the rest of that setup.",
      "Note this 'reference shot' in your file naming (e.g., 'IMG_0420_spyder_ref.cr3'). All subsequent shots from THIS setup will be color-corrected against it.",
      "If you change the lighting (move to a different room, sun goes behind clouds, swap a strobe modifier), shoot another reference frame. One reference = one lighting condition."
    ],
    spyder_lightroom_title: "Post workflow — Lightroom Classic (the most common case)",
    spyder_lightroom: [
      "Import the reference frame + all your shoot frames into Lightroom.",
      "Open the reference frame. Use the eyedropper / White Balance tool. Click on the LIGHT gray patch on the SpyderCHECKR (second-to-lightest on the grayscale strip — labeled '20% gray' on the card). White balance is now neutralized.",
      "Export the reference frame as a DNG file (File → Export, choose Digital Negative format).",
      "Open the SpyderCHECKR desktop software. Drag the DNG in. The software auto-detects the 24 patches and builds a custom DCP color profile based on your camera + lighting.",
      "Save the profile with a descriptive name (e.g., 'WeddingChurch_StrobeKey_Apr2026.dcp'). The software automatically places it where Lightroom can find it.",
      "Back in Lightroom: select the reference frame, go to the Develop module → Profile dropdown → choose your new custom profile. Skin tones and product colors now read perfectly accurate.",
      "Select all other shots from THIS lighting setup → right-click → Develop Settings → Sync → check 'Profile' and 'White Balance' → Sync. Done."
    ],
    spyder_pro: "Pro insight: the SpyderCHECKR's GRAY patch still does everything a regular gray card does (exposure, white balance, multi-camera sync). The 24-color upgrade is ONLY needed when color accuracy matters more than 'close enough'. Carry both: gray card lives in the camera bag, SpyderCHECKR lives in the studio kit.",
    spyder_other_software: "For Capture One: use the SpyderCHECKR Capture One Style instead of a DCP. For Photoshop / Premiere: export an ICC profile from the SpyderCHECKR software and apply it as a Camera Raw filter or LUT.",

    summary_title: "Why every photographer should carry one",
    summary_bullets: [
      "Pocket-sized ($15-30) and indestructible",
      "Solves exposure problems no amount of post can fix",
      "Solves color-accuracy problems no AWB can solve",
      "Doubles as a focus verification target",
      "Works under ANY light source — sun, flash, LED, tungsten, mixed",
      "One 1-second card shot can save hours of editing per session",
    ],
    closing: "The gray card is the cheapest, simplest, most powerful tool in professional photography. Beginners skip it. Professionals never leave home without it.",
  },
  es: {
    page_title: "La Tarjeta Gris: Precision de Color y Exposicion",
    page_sub: "Un pedazo plano y neutro de carton que refleja exactamente el 18% de la luz que recibe. Suena simple — pero es la herramienta mas confiable para clavar exposicion y color en cualquier genero: producto, retrato, lifestyle o comercial.",
    download: "Descargar como PDF",
    generating: "Generando...",

    intro_title: "Que es una Tarjeta Gris al 18%?",
    intro_body: "Una tarjeta gris al 18% es un objetivo de referencia plano y mate que refleja el 18% de la luz que la golpea. Parece carton gris ordinario. Pero ese 18% exacto de reflectancia es para lo que esta calibrado TODO fotometro de camara en la Tierra: cuando apuntas la camara al mundo, el medidor asume que el tono promedio es gris 18% y configura la exposicion para que ESE tono salga como gris medio en la imagen. Si tu escena realmente ES gris medio (porque la tarjeta llena el cuadro), el medidor acierta. Si tu escena NO lo es (una novia de blanco, un gato negro sobre carbon), el medidor falla — y la tarjeta lo corrige.",
    intro_uses: [
      "Exposicion perfecta en escenas de alto contraste (nieve blanca, fondos de estudio oscuros)",
      "Calibrar balance de blancos bajo cualquier luz — LED, flash, tungsteno + luz dia mezclados",
      "Puente de color entre multiples camaras (DSLR + smartphone + dron) en post",
      "Detectar hot spots, dominantes de color y reflejos no deseados en productos brillantes",
      "Confirmar enfoque perfecto a f/8-f/11 usando las marcas de alto contraste de la tarjeta",
    ],
    intro_visual_caption: "El gris 18% se ubica exactamente entre el negro puro y el blanco puro en luminancia — el 'medio' neutro que tu camara espera.",

    meter_trap_title: "1. Vencer la Trampa del Fotometro Interno",
    meter_trap_body: "Los fotometros promedian la escena al gris 18%. Eso funciona para una escena promedio. Falla cuando la escena esta dominada por tonos muy brillantes u oscuros — porque la camara intentara empujar ese tono dominante HACIA gris 18%, arruinando la exposicion.",
    meter_trap_examples: [
      { scene: "Novia de blanco contra pared blanca", trap: "El fotometro ve 'demasiado brillante' → subexpone → el vestido sale gris", fix: "Pon la tarjeta en la misma luz, mide SOBRE la tarjeta, bloquea esa exposicion, despues dispara a la novia." },
      { scene: "Gato negro sobre sofa negro", trap: "El fotometro ve 'demasiado oscuro' → sobreexpone → el pelo sale gris embarrado", fix: "Mismo procedimiento: mide sobre la tarjeta sostenida en la misma luz que el gato, bloquea exposicion, recompon." },
      { scene: "Paisaje montañoso nevado", trap: "El fotometro subexpone la nieve hasta convertirla en lodo gris", fix: "Pon la tarjeta sobre la nieve, toma lectura, usa esos ajustes — la nieve sale propiamente blanca." },
      { scene: "Silueta a contraluz al atardecer", trap: "El fotometro expone para el cielo brillante → el sujeto sale negro absoluto", fix: "Mide sobre la tarjeta sostenida en la MISMA luz ambiente del sujeto (no el cielo), y la cara saldra correctamente expuesta." },
    ],
    meter_trap_workflow_title: "Flujo de medicion en 5 pasos",
    meter_trap_workflow: [
      "Cambia la camara a modo de Medicion Puntual (Spot)",
      "Sosten la tarjeta gris en la MISMA luz que tu sujeto (no en sombra si el sujeto esta al sol)",
      "Llena el visor con la tarjeta y presiona el boton de medicion (medio presionar disparador o AE-L)",
      "Anota la exposicion recomendada (o usa AE-Lock para retenerla)",
      "Retira la tarjeta, recompone sobre tu sujeto y dispara. La exposicion ahora es correcta para ESA luz, sin importar que tan claro u oscuro sea el sujeto.",
    ],

    wb_title: "2. Maestria en Balance de Blancos Custom",
    wb_body: "Cada fuente de luz tiene su firma de color. Los LEDs suelen ser ligeramente verdes; el tungsteno es naranja; el flash es azul-neutro; la luz dia nublada es azul-fria. El Balance de Blancos Automatico adivina — y adivina mal en luz mixta. La tarjeta gris le da la verdad a la camara: 'aqui hay algo que SABES debe ser neutro. Ajusta todo lo demas en base a esto'.",
    wb_steps_title: "Como configurar WB Custom con tarjeta gris",
    wb_steps: [
      "Configura el modo de camara que vas a usar en la sesion (manual, prioridad apertura — no importa para WB)",
      "Sosten la tarjeta gris en la luz exacta donde estara tu sujeto. Llena el cuadro con la tarjeta (sin otros elementos).",
      "Toma UNA foto de la tarjeta. La imagen se ve gris — ligeramente teñida por la luz ambiente.",
      "Menu de camara → Balance de Blancos → Custom WB → 'Configurar desde imagen' → selecciona la foto que acabas de tomar.",
      "La camara ahora aplica un WB preciso calibrado a ESTA luz. Toda foto siguiente tendra color exacto.",
      "Repite la toma de tarjeta si te mueves a otro cuarto, el sol cambia, o la iluminacion cambia.",
    ],
    wb_genres_title: "Beneficios por genero",
    wb_genres: [
      { name: "Fotografia de producto", body: "Los tonos de piel no importan, pero el color exacto del producto lo es todo. Una tarjeta gris en CADA nuevo setup de luz es innegociable para trabajo de e-commerce." },
      { name: "Retrato", body: "Los tonos de piel viven o mueren con el balance de blancos. Una toma de 1 segundo a la tarjeta al inicio de la sesion ahorra horas de correccion de piel en Lightroom." },
      { name: "Lifestyle / eventos", body: "La luz mixta (ventana + LEDs interiores + velas) confunde a cualquier AWB. Custom WB con tarjeta da tonos de piel consistentes durante todo el evento." },
      { name: "Bienes raices / interiores", body: "Lamparas de tungsteno + luz dia + cocina fluorescente = tres dominantes de color en un solo cuadro. Una toma de tarjeta te permite corregir por lotes toda la sesion a una sola linea neutra." },
    ],

    multicam_title: "3. Sincronizacion de Color Multi-Camara",
    multicam_body: "Las sesiones modernas suelen mezclar cuerpos: una DSLR para tomas hero, un smartphone para tras camaras, un dron para aereas, la mirrorless de un amigo para un segundo angulo. Cada sensor renderiza el color distinto. Sin referencia, las ediciones se ven como un mosaico. Con una tarjeta gris tienes un 'diapason visual' que ancla cada camara a la misma linea neutra.",
    multicam_steps: [
      "Al inicio de la sesion, reune todas las camaras que se usaran.",
      "En la misma luz, haz que cada camara dispare UN frame de la tarjeta gris llenando el cuadro.",
      "Etiqueta o anota que frame corresponde a que camara.",
      "En Lightroom o Capture One, usa el cuentagotas sobre la tarjeta gris en el frame de referencia de cada camara. Configura eso como el perfil de WB de esa camara.",
      "Aplica ese perfil de WB a todos los demas frames de ESA camara en la sesion.",
      "Resultado: todas las camaras comparten la MISMA linea neutra. Los tonos de piel, colores de producto y look ambiente coinciden en toda la edicion.",
    ],

    gloss_title: "4. Manejo de Superficies Brillantes y Reflectantes",
    gloss_body: "Plastico, vidrio, metal pulido, joyeria, electronica y empaques laqueados de cosmeticos reflejan la luz de vuelta a la camara. Peor: reflejan el COLOR de los objetos cercanos (un sofa rojo reflejandose en el lado de una botella blanca de perfume = contaminacion rosa). Una tarjeta gris es el 'testigo' mas limpio para detectar estos problemas antes de que arruinen la toma.",
    gloss_steps: [
      "Coloca la tarjeta gris junto a (o detras de) el producto reflectante de modo que reciba la misma trayectoria de luz.",
      "Toma un cuadro de prueba. Observa la tarjeta: muestra una dominante de color? Un tinte azul significa que hay luz azul derramandose en el set (ventana fria, pared azul). Un tinte verde significa LEDs. Un tinte rojo significa un rebote calido.",
      "Si la tarjeta esta teñida, el producto esta siendo contaminado por ese MISMO tinte — aunque tu ojo no lo note.",
      "Corrige el derrame: cambia el rebote ofensor, mata la fuga con una bandera negra, cambia el LED, o corrige color en post usando la tarjeta como referencia neutra.",
      "Para hot spots: angula la tarjeta para imitar la superficie del producto. Si la tarjeta muestra un reflejo brillante de una fuente de luz a ese angulo, el producto tambien. Mueve la luz o agrega difusor.",
    ],
    gloss_examples_title: "Donde mas importa",
    gloss_examples: [
      "Relojes, joyeria y accesorios cromados",
      "Carcasas brillantes de plastico de telefonos y laptops",
      "Botellas de vino, perfume y empaques de cosmeticos",
      "Muebles laqueados, autos y pintura de motocicletas",
      "Headshots donde el sujeto usa lentes (catchlights vs. brillos no deseados)",
    ],

    focus_title: "5. Verificacion de Enfoque y Nitidez",
    focus_body: "Muchas tarjetas grises profesionales incluyen marcas de alto contraste — barras negras, lineas finas de cuadricula, o texto nitido impreso en la superficie gris. Estas marcas son oro para verificar si tu camara logra enfoque PERFECTO, especialmente a aperturas mas cerradas (f/8, f/11, f/16) donde los errores leves de enfoque se esconden facilmente.",
    focus_steps: [
      "Monta la camara en tripode, tarjeta gris a la misma distancia a la que dispararias tu sujeto real.",
      "Usa el punto central de enfoque de la camara sobre las marcas de alto contraste de la tarjeta.",
      "Dispara a f/2.8 (poca profundidad), f/5.6, f/8, y f/11. Un frame en cada apertura.",
      "Acerca cada foto al 100%. Las marcas deben verse navaja-nitidas.",
      "Si un frame esta suave → tu lente tiene problemas de calibracion front-focus o back-focus a esa apertura. La mayoria de cuerpos pro tienen AF micro-ajuste (Canon: AFMA, Nikon: AF Fine Tune, Sony: AF Micro Adj).",
      "Usa las marcas de la tarjeta + AFMA manual para afinar cada lente cuerpo-por-cuerpo para resultados navaja-nitidos.",
    ],
    focus_pro: "Tip pro: el 95% de las fotos 'suaves' a f/11 son en realidad un problema de enfoque, NO de difraccion. Verifica con tarjeta gris antes de culpar al lente.",

    spyder_title: "6. Subir de Nivel: El Color Checker de 24 Parches (Datacolor SpyderCHECKR 24)",
    spyder_body: "Una tarjeta gris resuelve dos cosas: exposicion y balance de blancos. El Datacolor SpyderCHECKR 24 resuelve una TERCERA: precision de color exacta y repetible en los 24 parches impresos — incluyendo seis tonos de piel, primarios, secundarios y una franja de grises que incluye la misma referencia de gris 18% que ya conoces. Pienselo como la tarjeta gris mejorada con una paleta de color calibrada incorporada.",
    spyder_what: [
      "24 parches de color calibrados: 18 objetivos de color + 6 pasos de escala de grises (incluyendo gris medio 18%, blanco puro y negro puro).",
      "Tamaño bolsillo (~10 x 15 cm), se dobla a la mitad para proteccion, cuelga de un cordon.",
      "Incluye el software SpyderCHECKR de Datacolor (gratis con la tarjeta) para crear Perfiles de Color DNG (DCP) personalizados para Lightroom Classic, o perfiles ICC para Capture One y Photoshop.",
      "Mucho mas poderoso que una tarjeta gris sola — pero todo el flujo de trabajo de tarjeta gris que acabas de aprender sigue funcionando usando el parche gris del SpyderCHECKR."
    ],
    spyder_when: [
      "Fotografia de producto y e-commerce donde el color exacto es lo principal",
      "Trabajo de retrato en estudio donde los tonos de piel deben coincidir entre sesiones",
      "Reproduccion de bellas artes (copiar pinturas, documentos antiguos, muestras de tela)",
      "Produccion de video donde una escena se grada y el resto debe coincidir",
      "Cuando el cliente dice 'iguala el color de este objeto fisico exactamente'"
    ],
    spyder_workflow_title: "Flujo en campo — durante la sesion",
    spyder_workflow: [
      "Al INICIO de cada nuevo montaje de iluminacion, coloca el SpyderCHECKR en la MISMA luz que tu sujeto. Sostenlo frente a la camara, sin sombra ni reflejos sobre el.",
      "Llena el 30-50% del cuadro con la tarjeta (NO necesita llenar todo el cuadro — solo lo suficiente para leer cada parche en software despues).",
      "Toma UNA foto con la misma configuracion de camara (modo manual preferido) que usaras para el resto del montaje.",
      "Anota esta 'toma de referencia' en el nombre del archivo (ej. 'IMG_0420_spyder_ref.cr3'). Todas las tomas siguientes de ESE montaje se corregiran contra ella.",
      "Si cambias la iluminacion (te mueves a otra sala, el sol se mete tras nubes, cambias un modificador de strobe), toma otra foto de referencia. Una referencia = una condicion de luz."
    ],
    spyder_lightroom_title: "Flujo en post — Lightroom Classic (el caso mas comun)",
    spyder_lightroom: [
      "Importa la toma de referencia + todas las tomas de la sesion en Lightroom.",
      "Abre la toma de referencia. Usa la herramienta de gotero / Balance de Blancos. Haz clic en el parche gris CLARO del SpyderCHECKR (el segundo mas claro de la franja gris — etiquetado '20% gray' en la tarjeta). El balance de blancos queda neutralizado.",
      "Exporta la toma de referencia como archivo DNG (Archivo → Exportar, formato Digital Negative).",
      "Abre el software SpyderCHECKR de escritorio. Arrastra el DNG. El software auto-detecta los 24 parches y construye un perfil de color DCP personalizado basado en tu camara + iluminacion.",
      "Guarda el perfil con un nombre descriptivo (ej. 'IglesiaBoda_StrobeKey_Abr2026.dcp'). El software lo coloca automaticamente donde Lightroom puede encontrarlo.",
      "De vuelta en Lightroom: selecciona la toma de referencia, ve al modulo Revelar → menu desplegable Perfil → elige tu nuevo perfil personalizado. Los tonos de piel y colores de producto ahora leen con precision perfecta.",
      "Selecciona todas las otras tomas de ESE montaje de luz → clic derecho → Configuracion de Revelar → Sincronizar → marca 'Perfil' y 'Balance de Blancos' → Sincronizar. Listo."
    ],
    spyder_pro: "Insight pro: el parche GRIS del SpyderCHECKR sigue haciendo todo lo que hace una tarjeta gris normal (exposicion, balance de blancos, sincronizacion multi-camara). La mejora de 24 colores SOLO se necesita cuando la precision de color importa mas que 'aproximado'. Lleva ambos: la tarjeta gris vive en la mochila, el SpyderCHECKR en el kit de estudio.",
    spyder_other_software: "Para Capture One: usa el Estilo de Capture One del SpyderCHECKR en vez de un DCP. Para Photoshop / Premiere: exporta un perfil ICC desde el software SpyderCHECKR y aplicalo como filtro Camera Raw o LUT.",

    summary_title: "Por que todo fotografo deberia cargar una",
    summary_bullets: [
      "Tamaño bolsillo ($15-30) e indestructible",
      "Resuelve problemas de exposicion que ningun post puede arreglar",
      "Resuelve problemas de precision de color que ningun AWB puede resolver",
      "Sirve tambien como objetivo de verificacion de enfoque",
      "Funciona bajo CUALQUIER fuente de luz — sol, flash, LED, tungsteno, mixta",
      "Una toma de 1 segundo de la tarjeta puede ahorrar horas de edicion por sesion",
    ],
    closing: "La tarjeta gris es la herramienta mas barata, simple y poderosa en la fotografia profesional. Los principiantes la saltan. Los profesionales nunca salen de casa sin ella.",
  },
};

export default function GrayCardPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `gray-card-${lang}.pdf`, [lang]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="gray-card-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">{c.page_title}</h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="gray-card-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Intro + visual */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Square weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.intro_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.intro_body}</p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-2">
              <GrayCardVisual lang={lang} />
            </div>
            <p className="text-[#71717A] text-xs italic text-center mb-4">{c.intro_visual_caption}</p>
            <ul className="space-y-1.5">
              {c.intro_uses.map((u, i) => (
                <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                  <span className="text-[#F5A524]">•</span>
                  {u}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 1. Meter trap */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sun weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.meter_trap_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.meter_trap_body}</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {c.meter_trap_examples.map((ex, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded p-3">
                  <p className="text-[#F5A524] font-medium text-sm mb-1">{ex.scene}</p>
                  <p className="text-rose-300/80 text-xs mb-1.5"><span className="font-semibold">Trap: </span>{ex.trap}</p>
                  <p className="text-emerald-300/80 text-xs"><span className="font-semibold">Fix: </span>{ex.fix}</p>
                </div>
              ))}
            </div>
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.meter_trap_workflow_title}</p>
            <ol className="space-y-2">
              {c.meter_trap_workflow.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* 2. Custom WB */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.wb_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.wb_body}</p>
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.wb_steps_title}</p>
            <ol className="space-y-2 mb-4">
              {c.wb_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.wb_genres_title}</p>
            <div className="space-y-2">
              {c.wb_genres.map((g, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded p-3">
                  <p className="text-[#F5A524] font-medium text-sm mb-1">{g.name}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{g.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 3. Multi-camera */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Camera weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.multicam_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.multicam_body}</p>
            <ol className="space-y-2">
              {c.multicam_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* 4. Gloss / reflective */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.gloss_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.gloss_body}</p>
            <ol className="space-y-2 mb-4">
              {c.gloss_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
            <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.gloss_examples_title}</p>
            <ul className="space-y-1.5">
              {c.gloss_examples.map((e, i) => (
                <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                  <span className="text-[#F5A524]">•</span>
                  {e}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 5. Focus verification */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Crosshair weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.focus_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.focus_body}</p>
            <ol className="space-y-2 mb-4">
              {c.focus_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
            <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3">
              <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.focus_pro}</p>
            </div>
          </motion.div>

          {/* 6. SpyderCHECKR 24 — the next-level color tool */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <GridFour weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.spyder_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.spyder_body}</p>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <SpyderChecker24Visual lang={lang} />
              <div>
                <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">
                  {lang === "es" ? "Que es" : "What's on the card"}
                </p>
                <ul className="space-y-1.5">
                  {c.spyder_what.map((w, i) => (
                    <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                      <span className="text-[#F5A524] shrink-0">•</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-5">
              <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">
                {lang === "es" ? "Cuando usarlo (no en cada sesion)" : "When to use it (not every shoot)"}
              </p>
              <ul className="space-y-1.5">
                {c.spyder_when.map((w, i) => (
                  <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                    <span className="text-[#F5A524] shrink-0">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-5">
              <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.spyder_workflow_title}</p>
              <ol className="space-y-2">
                {c.spyder_workflow.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mb-5">
              <p className="text-[#F5A524] text-xs uppercase tracking-widest font-semibold mb-2">{c.spyder_lightroom_title}</p>
              <ol className="space-y-2">
                {c.spyder_lightroom.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                    <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3 mb-3">
              <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.spyder_pro}</p>
            </div>
            <p className="text-[#71717A] text-xs leading-relaxed italic">{c.spyder_other_software}</p>
          </motion.div>

          {/* Summary card */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.summary_title}</h2>
            <ul className="space-y-1.5 mb-3">
              {c.summary_bullets.map((b, i) => (
                <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                  <span className="text-[#F5A524]">✓</span>
                  {b}
                </li>
              ))}
            </ul>
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.closing}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Hidden printable version */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.intro_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.intro_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          {c.intro_uses.map((u, i) => <li key={i} className="text-xs text-gray-700">{u}</li>)}
        </ul>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.meter_trap_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.meter_trap_body}</p>
        {c.meter_trap_examples.map((ex, i) => (
          <p key={i} className="text-xs text-gray-700 mb-1.5"><strong>{ex.scene}:</strong> <em>Trap — </em>{ex.trap}. <em>Fix — </em>{ex.fix}</p>
        ))}
        <p className="text-sm font-bold mt-2">{c.meter_trap_workflow_title}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-3">
          {c.meter_trap_workflow.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.wb_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.wb_body}</p>
        <p className="text-sm font-bold">{c.wb_steps_title}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-2">
          {c.wb_steps.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>
        <p className="text-sm font-bold">{c.wb_genres_title}</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          {c.wb_genres.map((g, i) => <li key={i} className="text-xs text-gray-700"><strong>{g.name}:</strong> {g.body}</li>)}
        </ul>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.multicam_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.multicam_body}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-3">
          {c.multicam_steps.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.gloss_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.gloss_body}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-2">
          {c.gloss_steps.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>
        <p className="text-sm font-bold">{c.gloss_examples_title}</p>
        <ul className="list-disc ml-5 space-y-1 mb-3">
          {c.gloss_examples.map((e, i) => <li key={i} className="text-xs text-gray-700">{e}</li>)}
        </ul>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.focus_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.focus_body}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-2">
          {c.focus_steps.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>
        <p className="text-xs text-gray-700 italic mb-3"><strong>Pro tip:</strong> {c.focus_pro}</p>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.spyder_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.spyder_body}</p>
        <ul className="list-disc ml-5 space-y-1 mb-2">
          {c.spyder_what.map((w, i) => <li key={i} className="text-xs text-gray-700">{w}</li>)}
        </ul>
        <p className="text-xs font-bold text-gray-800 mb-1">{lang === "es" ? "Cuando usarlo:" : "When to use it:"}</p>
        <ul className="list-disc ml-5 space-y-1 mb-2">
          {c.spyder_when.map((w, i) => <li key={i} className="text-xs text-gray-700">{w}</li>)}
        </ul>
        <p className="text-sm font-bold mt-2">{c.spyder_workflow_title}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-2">
          {c.spyder_workflow.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>
        <p className="text-sm font-bold">{c.spyder_lightroom_title}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-2">
          {c.spyder_lightroom.map((s, i) => <li key={i} className="text-xs text-gray-700">{s}</li>)}
        </ol>
        <p className="text-xs text-gray-700 italic mb-1"><strong>Pro insight:</strong> {c.spyder_pro}</p>
        <p className="text-xs text-gray-500 italic mb-3">{c.spyder_other_software}</p>

        <h2 className="text-lg font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.summary_title}</h2>
        <ul className="list-disc ml-5 space-y-1 mb-2">
          {c.summary_bullets.map((b, i) => <li key={i} className="text-xs text-gray-700">{b}</li>)}
        </ul>
        <p className="text-xs text-gray-700 italic">{c.closing}</p>

        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
